/**
 * RC Cola Video Gallery - JavaScript
 * Handles Swiper carousel, video lightbox, and YouTube integration
 */

(function() {
  'use strict';

  // Wait for DOM and Swiper to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const DEBUG_MODE = isDebugEnabled();

  function init() {
    // Check if Swiper is loaded
    if (typeof Swiper === 'undefined') {
      console.error('Video Gallery: Swiper.js is required but not loaded.');
      return;
    }

    const sections = document.querySelectorAll('[data-video-gallery-section]');
    if (!sections.length) return;

    sections.forEach(sectionEl => {
      const feedConfig = getFeedConfig(sectionEl);

      if (feedConfig.enabled && feedConfig.url) {
        loadFeed(sectionEl, feedConfig)
          .catch(() => false)
          .finally(() => {
            initSwiper(sectionEl);
            initLightbox(sectionEl);
          });
      } else {
        initSwiper(sectionEl);
        initLightbox(sectionEl);
      }
    });
  }

  function isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);
      const value = (params.get('vg_debug') || '').toLowerCase();
      return value === '1' || value === 'true' || value === 'yes';
    } catch (error) {
      return false;
    }
  }

  function logDebug(message, data) {
    if (!DEBUG_MODE) return;
    if (typeof data !== 'undefined') {
      console.info('[Video Gallery]', message, data);
    } else {
      console.info('[Video Gallery]', message);
    }
  }

  function setFeedStatus(sectionEl, status, detail) {
    if (!sectionEl) return;
    sectionEl.dataset.feedStatus = status;
    if (detail) {
      sectionEl.dataset.feedDetail = String(detail).slice(0, 240);
    } else {
      delete sectionEl.dataset.feedDetail;
    }
  }

  function scheduleSmoothScrollRefresh(reason) {
    if (typeof window.refreshSmoothScroll === 'function') {
      window.refreshSmoothScroll(reason);
    }
  }

  function refreshAfterImages(container, reason) {
    if (!container) return;
    const images = Array.from(container.querySelectorAll('img'));
    if (!images.length) return;

    let pending = 0;
    const onDone = () => {
      pending -= 1;
      if (pending <= 0) scheduleSmoothScrollRefresh(reason);
    };

    images.forEach(img => {
      if (img.complete) return;
      pending += 1;
      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', onDone, { once: true });
    });

    if (pending === 0) {
      scheduleSmoothScrollRefresh(reason);
    }
  }

  function getFeedConfig(sectionEl) {
    const enabled = sectionEl.dataset.feedEnabled === 'true';
    const handle = (sectionEl.dataset.feedHandle || '').trim();
    let url = (sectionEl.dataset.feedUrl || '').trim();
    const maxItems = Number.parseInt(sectionEl.dataset.feedMaxItems, 10);
    const proxyUrl = (sectionEl.dataset.feedProxyUrl || '').trim();

    if (!url && handle) {
      const sanitizedHandle = handle.replace(/^@/, '');
      url = `https://www.youtube.com/feeds/videos.xml?user=${encodeURIComponent(sanitizedHandle)}`;
    }

    return {
      enabled,
      url,
      maxItems: Number.isFinite(maxItems) ? maxItems : 0,
      proxyUrl,
    };
  }

  function loadFeed(sectionEl, feedConfig) {
    const swiperWrapper = sectionEl.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return Promise.resolve(false);

    const requestUrl = feedConfig.proxyUrl
      ? `${feedConfig.proxyUrl}${encodeURIComponent(feedConfig.url)}`
      : feedConfig.url;

    logDebug('Loading RSS feed', { requestUrl, feedConfig });
    setFeedStatus(sectionEl, 'loading');

    return fetch(requestUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(xmlText => {
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'text/xml');
        const parseErrors = xml.getElementsByTagName('parsererror');
        if (parseErrors.length) {
          throw new Error('RSS XML parse error');
        }
        const entries = parseFeedEntries(xml);

        if (!entries.length) {
          logDebug('RSS parsed with zero entries.');
          setFeedStatus(sectionEl, 'empty', 'No entries found in RSS feed');
          return false;
        }

        const maxItems = feedConfig.maxItems > 0 ? feedConfig.maxItems : entries.length;
        const items = entries.slice(0, maxItems);

        resetSwiperMarkup(swiperWrapper);
        swiperWrapper.innerHTML = items.map(buildVideoCardMarkup).join('');
        sectionEl.dataset.slideCount = String(items.length);
        setFeedStatus(sectionEl, 'loaded', `${items.length} items`);
        logDebug('RSS loaded', { count: items.length });
        return true;
      })
      .catch(error => {
        setFeedStatus(sectionEl, 'error', error && error.message ? error.message : 'RSS load failed');
        console.warn('Video Gallery: Failed to load YouTube RSS feed.', error);
        if (!feedConfig.proxyUrl) {
          logDebug('Try setting a CORS proxy URL in the section settings.');
        }
        return false;
      });
  }

  function parseFeedEntries(xml) {
    const entries = Array.from(xml.getElementsByTagName('entry'));

    return entries
      .map(entry => {
        const titleNode = entry.getElementsByTagName('title')[0];
        const title = titleNode ? titleNode.textContent.trim() : '';

        const idNode = entry.getElementsByTagName('yt:videoId')[0]
          || entry.getElementsByTagName('videoId')[0];
        let videoId = idNode ? idNode.textContent.trim() : '';

        let link = '';
        const linkNodes = Array.from(entry.getElementsByTagName('link'));
        for (const node of linkNodes) {
          if (node.getAttribute('rel') === 'alternate') {
            link = node.getAttribute('href') || '';
            break;
          }
        }

        if (!videoId && link) {
          videoId = getYouTubeId(link);
        }

        const thumbnailNode = entry.getElementsByTagName('media:thumbnail')[0]
          || entry.getElementsByTagName('thumbnail')[0];
        const thumbnailUrl = thumbnailNode ? thumbnailNode.getAttribute('url') : '';

        return {
          title,
          videoId,
          thumbnailUrl,
        };
      })
      .filter(item => item.videoId);
  }

  function buildVideoCardMarkup(item) {
    const safeTitle = item.title ? escapeHtml(item.title) : '';
    const thumbnailUrl = buildThumbnailUrl(item.videoId, item.thumbnailUrl);

    if (!thumbnailUrl) {
      return `
        <div class="swiper-slide video-card" data-video-id="${item.videoId}">
          <button class="video-card__button" aria-label="Play video${safeTitle ? `: ${safeTitle}` : ''}">
            <div class="video-card__placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="20" height="16" rx="2" stroke="white" stroke-width="2"/>
                <path d="M10 9L15 12L10 15V9Z" fill="white"/>
              </svg>
              <span>Video unavailable</span>
            </div>
          </button>
        </div>
      `;
    }

    return `
      <div class="swiper-slide video-card" data-video-id="${item.videoId}">
        <button class="video-card__button" aria-label="Play video${safeTitle ? `: ${safeTitle}` : ''}">
          <div class="video-card__thumbnail-wrapper">
            <img
              class="video-card__thumbnail"
              src="${thumbnailUrl}"
              alt="${safeTitle || 'Video thumbnail'}"
              loading="lazy"
              onerror="this.src='https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg'"
            >
            <div class="video-card__gradient"></div>
            <div class="video-card__play">
              <svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="34" cy="34" r="33" fill="rgba(255,255,255,0.9)" stroke="white" stroke-width="2"/>
                <path d="M28 22L46 34L28 46V22Z" fill="#EA242C"/>
              </svg>
            </div>
            ${safeTitle ? `<div class="video-card__title"><span>${safeTitle}</span></div>` : ''}
          </div>
        </button>
      </div>
    `;
  }

  function buildThumbnailUrl(videoId, thumbnailUrl) {
    if (thumbnailUrl) return thumbnailUrl;
    return getYouTubeThumbnail(videoId, 'maxresdefault') || '';
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function resetSwiperMarkup(swiperWrapper) {
    if (!swiperWrapper) return;

    const duplicates = swiperWrapper.querySelectorAll('.is-duplicate');
    duplicates.forEach(node => node.remove());
    swiperWrapper.removeAttribute('data-tripled');
    swiperWrapper.removeAttribute('data-original-count');
  }

  /**
   * Initialize Swiper Carousel
   */
  function initSwiper(sectionEl) {
    const swiperContainer = sectionEl.querySelector('.video-gallery__carousel');
    if (!swiperContainer) return;

    const prevButton = sectionEl.querySelector('.video-gallery__nav--prev');
    const nextButton = sectionEl.querySelector('.video-gallery__nav--next');
    const pagination = sectionEl.querySelector('.video-gallery__pagination');
    const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
    let initialSlideIndex = 0;
    let originalSlideCount = 0;

    if (swiperContainer.swiper) {
      swiperContainer.swiper.destroy(true, true);
      swiperContainer.swiper = null;
    }

    resetSwiperMarkup(swiperWrapper);

    if (swiperWrapper && !swiperWrapper.dataset.tripled) {
      const slides = Array.from(swiperWrapper.querySelectorAll('.swiper-slide'));
      const originalCount = slides.length;
      originalSlideCount = originalCount;

      if (originalCount > 0) {
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < 2; i += 1) {
          slides.forEach(slide => {
            const clone = slide.cloneNode(true);
            clone.classList.add('is-duplicate');
            fragment.appendChild(clone);
          });
        }

        swiperWrapper.appendChild(fragment);
        swiperWrapper.dataset.tripled = 'true';
        swiperWrapper.dataset.originalCount = String(originalCount);
        initialSlideIndex = originalCount;
      }
    } else if (swiperWrapper && swiperWrapper.dataset.originalCount) {
      originalSlideCount = Number(swiperWrapper.dataset.originalCount) || 0;
      initialSlideIndex = originalSlideCount;
    }

    const swiper = new Swiper(swiperContainer, {
      // Core settings
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 24,
      loop: true,
      loopAdditionalSlides: 2,
      initialSlide: initialSlideIndex,

      // Speed and easing
      speed: 500,

      // Grab cursor
      grabCursor: true,

      // Allow vertical page scroll while interacting with the carousel
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false,
      touchReleaseOnEdges: true,
      nested: true,

      // Pagination (custom)
      pagination: false,

      // Navigation
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },

      // Breakpoints for responsive behavior
      breakpoints: {
        // Mobile
        0: {
          slidesPerView: 1.3,
          spaceBetween: 16,
          centeredSlides: true,
        },
        // Tablet
        768: {
          slidesPerView: 2.5,
          spaceBetween: 20,
          centeredSlides: true,
        },
        // Desktop
        1024: {
          slidesPerView: 3.5,
          spaceBetween: 24,
          centeredSlides: true,
        },
        // Large Desktop
        1440: {
          slidesPerView: 4,
          spaceBetween: 24,
          centeredSlides: true,
        },
      },

      // Keyboard navigation
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Accessibility
      a11y: {
        prevSlideMessage: 'Previous video',
        nextSlideMessage: 'Next video',
        firstSlideMessage: 'This is the first video',
        lastSlideMessage: 'This is the last video',
        paginationBulletMessage: 'Go to video {{index}}',
      },
    });

    scheduleSmoothScrollRefresh('video-gallery:init');
    refreshAfterImages(swiperContainer, 'video-gallery:images');

    if (pagination && originalSlideCount > 0) {
      const totalSets = Math.max(1, Math.round(swiper.slides.length / originalSlideCount));

      const buildPagination = () => {
        pagination.innerHTML = '';
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < originalSlideCount; i += 1) {
          const bullet = document.createElement('button');
          bullet.type = 'button';
          bullet.className = 'swiper-pagination-bullet';
          bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);
          bullet.addEventListener('click', () => {
            let closestIndex = i;
            let closestDistance = Infinity;

            for (let setIndex = 0; setIndex < totalSets; setIndex += 1) {
              const candidate = i + originalSlideCount * setIndex;
              const distance = Math.abs(candidate - swiper.activeIndex);

              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = candidate;
              }
            }

            swiper.slideTo(closestIndex);
          });

          fragment.appendChild(bullet);
        }

        pagination.appendChild(fragment);
      };

      const updatePagination = () => {
        const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
        if (!bullets.length) return;

        let activeIndex = swiper.activeIndex - initialSlideIndex;
        if (originalSlideCount > 0) {
          activeIndex %= originalSlideCount;
          if (activeIndex < 0) activeIndex += originalSlideCount;
        }

        bullets.forEach((bullet, index) => {
          const isActive = index === activeIndex;
          bullet.classList.toggle('swiper-pagination-bullet-active', isActive);
          if (isActive) {
            bullet.setAttribute('aria-current', 'true');
          } else {
            bullet.removeAttribute('aria-current');
          }
        });
      };

      buildPagination();
      updatePagination();
      swiper.on('slideChange', updatePagination);
    }

    // Store swiper instance for potential future use
    swiperContainer.swiper = swiper;
  }

  /**
   * Initialize Video Lightbox
   */
  function initLightbox(sectionEl) {
    const lightbox = sectionEl.querySelector('.video-lightbox');
    if (!lightbox) return;

    const overlay = lightbox.querySelector('.video-lightbox__overlay');
    const closeBtn = lightbox.querySelector('.video-lightbox__close');
    const playerContainer = lightbox.querySelector('.video-lightbox__player');
    const videoCards = sectionEl.querySelectorAll('.video-card__button');

    // Click handlers for video cards
    videoCards.forEach(card => {
      card.addEventListener('click', function(e) {
        e.preventDefault();
        const videoCard = this.closest('.video-card');
        const videoId = videoCard.dataset.videoId;

        if (videoId) {
          openLightbox(videoId);
        }
      });
    });

    // Close handlers
    if (closeBtn) {
      closeBtn.addEventListener('click', closeLightbox);
    }

    if (overlay) {
      overlay.addEventListener('click', closeLightbox);
    }

    // Keyboard close (Escape key)
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && lightbox.getAttribute('aria-hidden') === 'false') {
        closeLightbox();
      }
    });

    /**
     * Open lightbox with YouTube video
     */
    function openLightbox(videoId) {
      // Create YouTube iframe
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
      iframe.title = 'YouTube video player';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;

      // Clear and add iframe
      playerContainer.innerHTML = '';
      playerContainer.appendChild(iframe);

      // Show lightbox
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus close button for accessibility
      setTimeout(() => {
        closeBtn.focus();
      }, 100);
    }

    /**
     * Close lightbox
     */
    function closeLightbox() {
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Stop video by clearing iframe after animation
      setTimeout(() => {
        playerContainer.innerHTML = '';
      }, 300);
    }
  }

  /**
   * Utility: Extract YouTube video ID from URL
   * Supports various YouTube URL formats
   */
  function getYouTubeId(url) {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/v\/([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  /**
   * Utility: Get YouTube thumbnail URL
   * No API key required - uses public thumbnail URLs
   */
  function getYouTubeThumbnail(videoId, quality = 'maxresdefault') {
    if (!videoId) return null;

    // Quality options: maxresdefault, sddefault, hqdefault, mqdefault, default
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  }

  // Expose utilities for potential external use
  window.RCColaVideoGallery = {
    getYouTubeId: getYouTubeId,
    getYouTubeThumbnail: getYouTubeThumbnail,
  };

})();
